import { useMemo } from 'react';
import { useLoaderData } from '@remix-run/react';
import type { FeedEntry } from '~/services/layout';
import {
  getButtonCellClassName,
  getButtonCellItemClassNames,
  getButtonCellUnderlineClassNames,
} from './get-class-names';
import { Underline } from './underline';
import { AssetImage } from './asset-image';
import { Title } from './title';
import { defaultButtonCellId } from '~/services/layout/layout';
import { getButtonCellFlexDirection } from './get-button-cell-flex-direction';

export type ButtonCellProps = {
  selected: boolean;
  cellIds: string[];
  entry: FeedEntry;
};

const getStylesByCellId = (cellId: string, cellStyles: any) => {
  return cellStyles[cellId]?.configuration?.styles || {};
};

const getInheritedCellIds = (
  cellIds: string[]
): {
  mobileCellId: string;
  tabletCellId: string;
  desktopCellId: string;
  largeDesktopCellId: string;
} => {
  let mobileCellId: string | undefined = cellIds[0];
  let tabletCellId: string | undefined = cellIds[1];
  let desktopCellId: string | undefined = cellIds[2];
  let largeDesktopCellId: string | undefined = cellIds[3];

  // Apply default for mobile if not set
  if (!mobileCellId) {
    mobileCellId = defaultButtonCellId;
  }

  // Apply inheritance pattern from getCellPluginConfigurationIds
  if (!tabletCellId) {
    tabletCellId = mobileCellId;
  }
  if (!desktopCellId) {
    desktopCellId = tabletCellId || mobileCellId;
  }
  if (!largeDesktopCellId) {
    largeDesktopCellId = desktopCellId || tabletCellId || mobileCellId;
  }

  return {
    mobileCellId: mobileCellId as string,
    tabletCellId: tabletCellId as string,
    desktopCellId: desktopCellId as string,
    largeDesktopCellId: largeDesktopCellId as string,
  };
};

export function ButtonCell(props: ButtonCellProps): JSX.Element {
  const selected = props.selected;

  const { cellStyles } = useLoaderData<any>();

  const inheritedCellIds = useMemo(
    () => getInheritedCellIds(props.cellIds),
    [props.cellIds]
  );

  const styles = useMemo(
    () => ({
      unset: null,
      mobile: getStylesByCellId(
        inheritedCellIds.mobileCellId || defaultButtonCellId,
        cellStyles
      ),
      tablet: getStylesByCellId(
        inheritedCellIds.tabletCellId || defaultButtonCellId,
        cellStyles
      ),
      desktop: getStylesByCellId(
        inheritedCellIds.desktopCellId || defaultButtonCellId,
        cellStyles
      ),
      'large-desktop': getStylesByCellId(
        inheritedCellIds.largeDesktopCellId || defaultButtonCellId,
        cellStyles
      ),
    }),
    [cellStyles, inheritedCellIds]
  );

  const inheritedCellIdsArray = useMemo(
    () => [
      inheritedCellIds.mobileCellId,
      inheritedCellIds.tabletCellId,
      inheritedCellIds.desktopCellId,
      inheritedCellIds.largeDesktopCellId,
    ],
    [inheritedCellIds]
  );

  const buttonStyles = getButtonCellItemClassNames(styles, selected);
  const flexDirectionClassName = getButtonCellFlexDirection(styles);

  return (
    <section
      className={`w-full min-w-0 ${getButtonCellClassName({
        cellIds: inheritedCellIdsArray,
      })}`}
    >
      <button
        type="button"
        className={`relative w-full min-w-0 ${buttonStyles}`}
      >
        <div
          className={`button-cell-content flex w-full min-w-0 items-center ${flexDirectionClassName}`}
        >
          <AssetImage selected={selected} styles={styles} entry={props.entry} />
          <Title selected={selected} styles={styles} entry={props.entry} />
        </div>
        <Underline classNames={getButtonCellUnderlineClassNames(selected)} />
      </button>
    </section>
  );
}
